import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle, AlertCircle, AlertTriangle,
  Package, RefreshCw, FileText, Users, Wifi,
  WifiOff, X, ShieldCheck, Search, ScanSearch, RotateCcw, ExternalLink,
  TrendingUp,
} from 'lucide-react';
import { Modal, PackageStats, ProgressBar } from '../../components/lieferung/LieferungComponents';
import { EanModal, MhdPromptModal, MhdInputModal } from '../../components/lieferung/LieferungModals';
import { PackageCard } from '../../components/lieferung/PackageCard';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// ─── Haupt-Komponente ─────────────────────────────────────────────────────────
export default function LieferungDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [delivery, setDelivery] = useState(null);
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([]);
  const [missingItems, setMissingItems] = useState([]);
  const [updating, setUpdating] = useState(null);

  // Tabak-Preisänderungen: { [fremd_article_number]: { old_price, new_price, diff, article_name } }
  const [tobaccoPriceChanges, setTobaccoPriceChanges] = useState({});
  const [completing, setCompleting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [alert, setAlert] = useState(null);
  const [openPak, setOpenPak] = useState(null);
  const [pakSearch, setPakSearch] = useState('');
  const [priceGate, setPriceGate] = useState(null);   // NEUPREIS-Kenntnisnahme-Dialog

  // SSE
  const [sseConnected, setSseConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const esRef = useRef(null);

  // Modal states
  const [mhdPromptItem, setMhdPromptItem] = useState(null);   // Step 1: MHD ja/nein?
  const [mhdInputItem, setMhdInputItem] = useState(null);     // Step 2: MHD eingeben
  const [eanItem, setEanItem] = useState(null);               // Step 3: EAN scannen
  const [colliEditPkg, setColliEditPkg] = useState(null);     // Colli-ID bearbeiten
  const [colliInput, setColliInput] = useState('');
  const [reparsingColli, setReparsingColli] = useState(false);  // Re-Parse Colli/Kategorie
  const [reparsingFull, setReparsingFull] = useState(false);    // Neu parsen (Keyword-Parser)
  const pendingRef = useRef({});                                // Extra data (actualQty, mhd, ean)
  const [skipMhdEans, setSkipMhdEans] = useState(new Set());   // EANs ohne MHD-Abfrage
  const [noMhdFremdNrs, setNoMhdFremdNrs] = useState(new Set()); // FremdArtNr mit has_no_mhd=true

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 4000);
  };

  // ── Skip-MHD-EANs laden ────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/api/admin/settings/skip-mhd-eans`, { headers: authHeader() })
      .then(r => setSkipMhdEans(new Set(r.data?.skip_mhd_eans || [])))
      .catch(() => {});
    axios.get(`${API}/api/lieferung/no-mhd-products`, { headers: authHeader() })
      .then(r => setNoMhdFremdNrs(new Set(r.data?.no_mhd_fremd_nrs || [])))
      .catch(() => {});
  }, []);


  const packageGroups = useMemo(() => {
    const byPkg = {};
    items.forEach(item => {
      const pkgId = item.package_id || `_virtual_${item.category || 'shop'}`;
      if (!byPkg[pkgId]) byPkg[pkgId] = [];
      byPkg[pkgId].push(item);
    });
    return Object.entries(byPkg).map(([pkgId, pkgItems]) => {
      const pkg = packages.find(p => p.id === pkgId);
      return {
        id: pkgId,
        pak_number: pkg?.pak_number || null,
        colli_id: pkg?.colli_id || '',
        category: pkg?.category || pkgItems[0]?.category || 'shop',
        items: pkgItems,
        isVirtual: !pkg,
      };
    }).sort((a, b) => {
      // Real packages first, sorted by pak_number; virtual last
      if (a.isVirtual && !b.isVirtual) return 1;
      if (!a.isVirtual && b.isVirtual) return -1;
      const aNum = parseInt(a.pak_number) || 0;
      const bNum = parseInt(b.pak_number) || 0;
      return aNum - bNum;
    });
  }, [packages, items]);

  const completedPakCount = useMemo(
    () => packageGroups.filter(g => {
      const visible = g.items.filter(i => !i.is_pfand);
      return visible.length > 0 && visible.every(i => i.status === 'checked');
    }).length,
    [packageGroups]
  );
  const totalPakCount = packageGroups.length;
  const allPaksComplete = totalPakCount > 0 && completedPakCount === totalPakCount;

  const checkedCount = items.filter(i => !i.is_pfand && i.status === 'checked').length;
  const totalCount = items.filter(i => !i.is_pfand).length;
  const diffCount = items.filter(i => i.has_difference).length;
  const isCompleted = delivery?.status === 'completed';
  const canReopen = isCompleted && ['admin', 'stationsleitung', 'superadmin'].includes(user?.role);

  // ── Suchfilter für Pakete ───────────────────────────────────────────────
  const filteredGroups = useMemo(() => {
    if (!pakSearch.trim()) return packageGroups;
    const q = pakSearch.trim().toLowerCase();
    return packageGroups.filter(g => {
      // Suche nach Paketnummer / Colli-ID
      const colli = (g.colli_id || '').toLowerCase();
      const pak = (g.pak_number || '').toLowerCase();
      if (colli.includes(q) || pak.includes(q)) return true;
      // Suche nach Artikelname oder Artikelnummer in den enthaltenen Waren
      return (g.items || []).some(item =>
        (item.article_name || item.name || '').toLowerCase().includes(q) ||
        (item.article_number || item.supplier_art_nr || item.fremd_article_number || '').toLowerCase().includes(q)
      );
    });
  }, [packageGroups, pakSearch]);

  // Pakete automatisch öffnen wenn durch Warensuche gefunden
  useEffect(() => {
    if (!pakSearch.trim() || filteredGroups.length === 0) return;
    const q = pakSearch.trim().toLowerCase();
    // Nur aufklappen wenn es eine Warensuche ist (keine Paketnummer)
    const isItemSearch = !filteredGroups.some(g =>
      (g.colli_id || '').toLowerCase().includes(q) ||
      (g.pak_number || '').toLowerCase().includes(q)
    );
    if (isItemSearch && filteredGroups.length <= 5) {
      // Alle gefundenen Pakete aufklappen
      setOpenPak(filteredGroups[0]?.id || null);
    }
  }, [pakSearch, filteredGroups]);

  // ── Daten laden ─────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      // Timeout nach 30 Sekunden – verhindert ewiges Laden
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);
      const r = await axios.get(`${API}/api/lieferung/deliveries/${id}/items`, {
        headers: authHeader(),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      setDelivery(r.data.delivery);
      setPackages(r.data.packages || []);
      setItems(r.data.items || []);
      setMissingItems(r.data.missing_items || []);
      // Tabak-Preisänderungen (fire-and-forget)
      axios.get(`${API}/api/tobacco/price-changes/${id}`, { headers: authHeader() })
        .then(res => { if (res.data?.changes) setTobaccoPriceChanges(res.data.changes); })
        .catch(() => {});
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        showAlert('error', 'Zeitüberschreitung – Server antwortet nicht. Bitte später erneut versuchen.');
      } else {
        showAlert('error', 'Lieferung konnte nicht geladen werden');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── SSE ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    const url = `${API}/api/lieferung/deliveries/${id}/events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;
    es.onopen = () => setSseConnected(true);
    es.onerror = () => setSseConnected(false);
    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        switch (msg.type) {
          case 'connected': setSseConnected(true); break;
          case 'item_updated':
            setItems(prev => prev.map(i => i.id === msg.data.item?.id ? { ...i, ...msg.data.item } : i));
            setDelivery(prev => prev ? { ...prev, checked_items: msg.data.checked_items } : prev);
            break;
          case 'delivery_completed':
            setDelivery(prev => prev ? { ...prev, status: 'completed', completed_at: msg.data.completed_at } : prev);
            showAlert('success', `Lieferung abgeschlossen von ${msg.data.completed_by_name || 'Mitarbeiter'}`);
            break;
          case 'delivery_reopened':
            setDelivery(prev => prev ? { ...prev, status: 'imported', completed_at: null } : prev);
            showAlert('success', `Lieferung wiedereröffnet von ${msg.data.reopened_by_name || 'Admin'}`);
            break;
          case 'users_update':
            setActiveUsers(msg.data.users || []);
            break;
          default: break;
        }
      } catch {}
    };
    return () => { es.close(); setSseConnected(false); };
  }, [id]);

  // ── Backend: Artikel abhaken ────────────────────────────────────────────
  const doCheck = useCallback(async (item, status, actualQuantity, ean, mhd, priceAck = false, priceReject = false) => {
    setUpdating(item.id);
    try {
      const payload = { status };
      if (actualQuantity != null) payload.actual_quantity = actualQuantity;
      if (ean) payload.ean = ean;
      if (mhd) payload.mhd_confirmed = mhd;
      if (priceAck) payload.price_change_acknowledged = true;
      if (priceReject) payload.price_change_rejected = true;

      const r = await axios.patch(`${API}/api/lieferung/items/${item.id}`, payload, { headers: authHeader() });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...r.data } : i));

      // MHD automatisch angelegt → Toast anzeigen
      if (r.data?.mhd_auto_created) {
        toast.success(`MHD automatisch angelegt: ${r.data.mhd_auto_product_name}`, {
          description: `Ablaufdatum ${mhd} wurde im MHD-Modul eingetragen.`,
          duration: 4000,
        });
      }

      if (ean && item.fremd_article_number) {
        await axios.post(`${API}/api/lieferung/ean`, {
          fremd_article_number: item.fremd_article_number,
          ean, article_name: item.article_name, item_id: item.id,
          article_number: item.article_number || '',
        }, { headers: authHeader() }).catch(() => {});
      }
    } catch (err) {
      // NEUPREIS-Kenntnisnahme erforderlich (HTTP 409) → Dialog mit zwei Optionen
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'price_change_unacknowledged') {
        setUpdating(null);
        setPriceGate({ item, status, actualQuantity, ean, mhd, change: detail.price_change || {} });
        return;
      }
      showAlert('error', 'Fehler beim Speichern');
    } finally {
      setUpdating(null);
    }
  }, []);

  // NEUPREIS-Gate: Mitarbeiter bestätigt Kenntnisnahme
  const handlePriceAck = useCallback(() => {
    setPriceGate(g => {
      if (g) doCheck(g.item, g.status, g.actualQuantity, g.ean, g.mhd, true, false);
      return null;
    });
  }, [doCheck]);

  // NEUPREIS-Gate: Mitarbeiter korrigiert Fehlalarm ("falsch zugeordnet")
  const handlePriceReject = useCallback(() => {
    setPriceGate(g => {
      if (g) doCheck(g.item, g.status, g.actualQuantity, g.ean, g.mhd, false, true);
      return null;
    });
  }, [doCheck]);

  // ── Check-Flow: Kategorie-abhängig (Package-Kategorie hat Vorrang) ──────────
  const handleCheck = useCallback((item, status, actualQuantity) => {
    // Unchecking → direct
    if (status === 'pending') {
      doCheck(item, 'pending', null, null, null);
      return;
    }

    // Effektive Kategorie: Package-Kategorie > Item-Kategorie
    const pkg = packageGroups.find(g => g.items.some(i => i.id === item.id));
    const effectiveCat = pkg?.category || item.category || 'shop';

    // TABAK: Direkt abhaken, kein EAN/MHD
    if (effectiveCat === 'tabak') {
      doCheck(item, 'checked', actualQuantity, null, null);
      return;
    }

    // FEHLMENGE = 0: Artikel nicht geliefert → direkt als geprüft markieren, kein EAN/MHD
    if (actualQuantity === 0) {
      doCheck(item, 'checked', 0, null, null);
      return;
    }

    // SHOP / FRISCHE: MHD-Prompt → MHD-Input → EAN → Check
    // Prüfen ob MHD für diesen Artikel übersprungen werden soll (has_no_mhd Flag)
    if (item.fremd_article_number && noMhdFremdNrs.has(item.fremd_article_number)) {
      doCheck(item, 'checked', actualQuantity, null, null);
      return;
    }
    // Legacy EAN-basierte Skip-Liste
    if (item.ean && skipMhdEans.has(item.ean)) {
      // MHD-Dialog überspringen – direkt abhaken (kein MHD)
      doCheck(item, 'checked', actualQuantity, null, null);
      return;
    }
    pendingRef.current = { actualQuantity, ean: null, mhd: null };
    setMhdPromptItem(item);
  }, [doCheck, packageGroups, noMhdFremdNrs, skipMhdEans]);

  // MHD Prompt → "Ja" (mit optionalem Wert) oder ins MHD-Input
  const handleMhdPromptYes = useCallback((prefilledMhd) => {
    const item = mhdPromptItem;
    setMhdPromptItem(null);
    if (prefilledMhd) {
      // MHD confirmed from PDF → proceed to EAN
      pendingRef.current.mhd = prefilledMhd;
      if (item.is_new_product && !item.ean) {
        setEanItem(item);
      } else {
        doCheck(item, 'checked', pendingRef.current.actualQuantity, null, prefilledMhd);
      }
    } else {
      // Show MHD input
      setMhdInputItem(item);
    }
  }, [mhdPromptItem, doCheck]);

  // MHD Prompt → "Nein" (kein MHD)
  const handleMhdPromptNo = useCallback(() => {
    const item = mhdPromptItem;
    setMhdPromptItem(null);
    pendingRef.current.mhd = null;
    if (item.is_new_product && !item.ean) {
      setEanItem(item);
    } else {
      doCheck(item, 'checked', pendingRef.current.actualQuantity, null, null);
    }
  }, [mhdPromptItem, doCheck]);

  // MHD Prompt → "Nein, niemals MHD" (Produkt permanent merken via fremd_article_number)
  const handleMhdNeverAsk = useCallback(async () => {
    const item = mhdPromptItem;
    const fremdNr = item?.fremd_article_number;
    const ean = item?.ean;
    setMhdPromptItem(null);
    pendingRef.current.mhd = null;

    // Primär: has_no_mhd Flag am Produkt setzen (fremd_article_number basiert)
    if (fremdNr) {
      setNoMhdFremdNrs(prev => new Set([...prev, fremdNr]));
      axios.post(`${API}/api/lieferung/products/set-no-mhd`,
        { fremd_article_number: fremdNr, article_name: item.article_name },
        { headers: authHeader() }
      ).catch(() => {});
      toast.success('Gemerkt – MHD wird für dieses Produkt nicht mehr abgefragt', { duration: 3000 });
    } else if (ean) {
      // Fallback: EAN-basierte Skip-Liste (Legacy)
      setSkipMhdEans(prev => new Set([...prev, ean]));
      axios.post(`${API}/api/admin/settings/skip-mhd-ean`, { ean }, { headers: authHeader() }).catch(() => {});
      toast.success('Gemerkt – MHD wird für dieses Produkt nicht mehr abgefragt', { duration: 3000 });
    }
    // Wie "Nein" behandeln
    if (item.is_new_product && !item.ean) {
      setEanItem(item);
    } else {
      doCheck(item, 'checked', pendingRef.current.actualQuantity, null, null);
    }
  }, [mhdPromptItem, doCheck]);

  // MHD Input → submitted
  const handleMhdSubmit = useCallback((mhd, articleNr) => {
    const item = mhdInputItem;
    setMhdInputItem(null);
    pendingRef.current.mhd = mhd;
    pendingRef.current.articleNr = articleNr || null;
    if (item.is_new_product && !item.ean) {
      setEanItem(item);
    } else {
      doCheck(item, 'checked', pendingRef.current.actualQuantity, null, mhd);
      // Art.-Nr. + Bestand-Eingang speichern (fire-and-forget, kein Fehler im Hauptflow)
      if (item.ean && (articleNr || item.gesamt_menge)) {
        const qty = pendingRef.current.actualQuantity ?? item.gesamt_menge ?? 0;
        axios.post(`${API}/api/inventory/receive`, {
          ean: item.ean,
          product_name: item.article_name || null,
          quantity_received: parseInt(qty, 10) || 1,
          delivery_id: id,
          delivery_date: delivery?.delivery_date || null,
          location_id: delivery?.location_id || null,
          article_number: articleNr || null,
        }, { headers: authHeader() }).catch(() => {});
        // FIX A: Falls keine manuelle Artikelnummer eingegeben → JET-Nr. aus Parsing-Daten übernehmen
        const effectiveArticleNr = articleNr || item.article_number || null;
        if (effectiveArticleNr) {
          axios.post(`${API}/api/article-nr/save`, {
            ean: item.ean,
            article_number: effectiveArticleNr,
            product_name: item.article_name || null,
            location_id: delivery?.location_id || null,
          }, { headers: authHeader() }).catch(() => {});
        }
      }
    }
  }, [mhdInputItem, doCheck, id, delivery]);

  // MHD Input → skip
  const handleMhdSkip = useCallback(() => {
    const item = mhdInputItem;
    setMhdInputItem(null);
    pendingRef.current.mhd = null;
    if (item.is_new_product && !item.ean) {
      setEanItem(item);
    } else {
      doCheck(item, 'checked', pendingRef.current.actualQuantity, null, null);
    }
  }, [mhdInputItem, doCheck]);

  // EAN → submitted
  const handleEanSubmit = useCallback((ean) => {
    const item = eanItem;
    setEanItem(null);
    doCheck(item, 'checked', pendingRef.current.actualQuantity, ean, pendingRef.current.mhd);
  }, [eanItem, doCheck]);

  // EAN → skip
  const handleEanSkip = useCallback(() => {
    const item = eanItem;
    setEanItem(null);
    doCheck(item, 'checked', pendingRef.current.actualQuantity, null, pendingRef.current.mhd);
  }, [eanItem, doCheck]);

  // ── Colli-ID bearbeiten ─────────────────────────────────────────────────
  const handleEditColli = useCallback((group) => {
    setColliEditPkg(group);
    setColliInput(group.colli_id || '');
  }, []);

  const handleSaveColli = useCallback(async () => {
    if (!colliEditPkg) return;
    const pkg = packages.find(p => p.pak_number === colliEditPkg.pak_number);
    if (!pkg) return;
    try {
      await axios.patch(`${API}/api/lieferung/packages/${pkg.id}/colli`, { colli_id: colliInput }, { headers: authHeader() });
      setPackages(prev => prev.map(p => p.id === pkg.id ? { ...p, colli_id: colliInput.trim() } : p));
      setColliEditPkg(null);
      showAlert('success', 'Colli-ID gespeichert');
    } catch {
      showAlert('error', 'Fehler beim Speichern der Colli-ID');
    }
  }, [colliEditPkg, colliInput, packages]);

  // ── Kategorie umschalten (Shop ↔ Tabak) ────────────────────────────────
  const handleToggleCategory = useCallback(async (group) => {
    const pkg = packages.find(p => p.pak_number === group.pak_number);
    if (!pkg) return;
    const newCat = pkg.category === 'tabak' ? 'shop' : 'tabak';
    try {
      const r = await axios.patch(`${API}/api/lieferung/packages/${pkg.id}/category`, { category: newCat }, { headers: authHeader() });
      setPackages(prev => prev.map(p => p.id === pkg.id ? {
        ...p,
        category: newCat,
        // Falls Colli-ID aus Rohtext gefunden wurde, direkt einblenden
        colli_id: r.data.colli_id || p.colli_id,
      } : p));
      showAlert('success', `Kategorie auf "${newCat === 'tabak' ? 'Tabak' : 'Shop'}" geändert`);
    } catch {
      showAlert('error', 'Fehler beim Ändern der Kategorie');
    }
  }, [packages]);

  // ── Colli-IDs & Kategorien neu laden (für falsch geparste Tabak-WBS) ────
  const handleReparseColli = async () => {
    setReparsingColli(true);
    try {
      const r = await axios.post(`${API}/api/lieferung/deliveries/${id}/reparse-colli`, {}, { headers: authHeader() });
      showAlert('success', r.data.message || 'Colli-IDs & Kategorien aktualisiert');
      await loadData();
    } catch (e) {
      showAlert('error', e.response?.data?.detail || 'Fehler beim Neu-Laden');
    } finally {
      setReparsingColli(false);
    }
  };

  // ── Vollständig neu parsen (Keyword-Parser auf gespeichertem PDF) ────────
  const handleReparseFull = async () => {
    if (!window.confirm('Alle Artikel dieser Lieferung werden gelöscht und aus dem Original-PDF neu geparst. Bereits geprüfte Artikel werden zurückgesetzt. Fortfahren?')) return;
    setReparsingFull(true);
    try {
      const r = await axios.post(`${API}/api/lieferung/deliveries/${id}/reparse-full`, {}, { headers: authHeader() });
      showAlert('success', r.data.message || `${r.data.items_count} Artikel erfolgreich neu geparst`);
      await loadData();
    } catch (e) {
      showAlert('error', e.response?.data?.detail || 'Fehler beim Neu-Parsen');
    } finally {
      setReparsingFull(false);
    }
  };

  // ── Lieferung abschließen ───────────────────────────────────────────────
  const handleComplete = async () => {
    setCompleting(true);
    try {
      const r = await axios.patch(`${API}/api/lieferung/deliveries/${id}/complete`, {}, { headers: authHeader() });
      setDelivery(r.data);
      showAlert('success', 'Lieferung erfolgreich abgeschlossen!');
    } catch (e) {
      showAlert('error', e.response?.data?.detail || 'Fehler beim Abschließen');
    } finally {
      setCompleting(false);
    }
  };

  // ── Lieferung wiedereröffnen (nur Admin / Stationsleitung) ──────────────
  const handleReopen = async () => {
    if (!window.confirm('Lieferung wiedereröffnen? Der Status wird auf "In Prüfung" zurückgesetzt und Artikel können erneut bearbeitet werden.')) return;
    setReopening(true);
    try {
      const r = await axios.patch(`${API}/api/lieferung/deliveries/${id}/reopen`, {}, { headers: authHeader() });
      setDelivery(r.data);
      showAlert('success', 'Lieferung erfolgreich wiedereröffnet');
    } catch (e) {
      showAlert('error', e.response?.data?.detail || 'Fehler beim Wiedereröffnen');
    } finally {
      setReopening(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-6 w-6 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!delivery) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <AlertCircle className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Lieferung nicht gefunden</p>
          <button onClick={() => navigate('/lieferung')} className="mt-4 text-sm text-primary hover:underline">Zurück</button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">

        {/* Alert */}
        {alert && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${alert.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}`} data-testid="detail-alert">
            {alert.type === 'success' ? <CheckCircle className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
            {alert.msg}
          </div>
        )}

        {/* ── Header-Card ─────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => navigate('/lieferung')} className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors" data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" /> Übersicht
            </button>
            <div className="flex items-center gap-3">
              {activeUsers.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400" data-testid="active-users">
                  <Users className="h-3.5 w-3.5" /> {activeUsers.join(', ')}
                </div>
              )}
              <div className="flex items-center gap-1 text-xs" data-testid="sse-indicator">
                {sseConnected ? <Wifi className="h-3.5 w-3.5 text-green-500" /> : <WifiOff className="h-3.5 w-3.5 text-gray-400" />}
                <span className={sseConnected ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                  {sseConnected ? 'Live' : 'Offline'}
                </span>
              </div>
            </div>
          </div>

          {/* Titel */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary flex-shrink-0" />
              WBS {delivery.wbs_number || '—'}
            </h1>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isCompleted ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'}`}>
              {isCompleted ? 'Abgeschlossen' : 'In Prüfung'}
            </span>
            {delivery.pdf_filename && (
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(
                      `${API}/api/lieferung/deliveries/${id}/original-pdf`,
                      { headers: authHeader(), responseType: 'blob' }
                    );
                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    window.open(url, '_blank');
                  } catch {
                    toast.error('PDF konnte nicht geladen werden');
                  }
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium transition-colors border border-blue-200 dark:border-blue-700"
                title={`Original-PDF öffnen: ${delivery.pdf_filename}`}
                data-testid="open-pdf-btn"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                PDF öffnen
              </button>
            )}
          </div>

          {/* Meta-Info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {[
              { label: 'Lieferant', value: delivery.supplier?.name || '—' },
              { label: 'Lieferdatum', value: delivery.delivery_date || '—' },
              { label: 'Differenzen', value: diffCount > 0 ? `${diffCount}` : '—', warn: diffCount > 0 },
              { label: 'Datei', value: (delivery.pdf_filename || '—').replace('.pdf', '') },
            ].map(({ label, value, warn }) => (
              <div key={label} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2.5">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</p>
                <p className={`text-sm font-medium truncate ${warn ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{value}</p>
              </div>
            ))}
          </div>

          <ProgressBar checked={checkedCount} total={totalCount} />

          {delivery.address_warning && (
            <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span><strong>Adress-Warnung:</strong> {delivery.address_warning}</span>
            </div>
          )}

          {isCompleted && (
            <div className="mt-3 flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium text-green-700 dark:text-green-300">Lieferung abgeschlossen</span>
              </div>
              {canReopen && (
                <button
                  onClick={handleReopen}
                  disabled={reopening}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50"
                  data-testid="reopen-delivery-btn"
                >
                  {reopening ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  Wiedereröffnen
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Paket-Statistik ─────────────────────────────────────── */}
        {totalPakCount > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <PackageStats completed={completedPakCount} total={totalPakCount} />
            </div>
            {!isCompleted && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleReparseFull}
                  disabled={reparsingFull}
                  title="Alle Artikel aus dem gespeicherten Original-PDF neu einlesen (Keyword-Parser)"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
                  data-testid="reparse-full-btn"
                >
                  {reparsingFull ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ScanSearch className="h-3.5 w-3.5" />}
                  Neu parsen
                </button>
                <button
                  onClick={handleReparseColli}
                  disabled={reparsingColli}
                  title="Colli-IDs & Kategorien aus PDF-Rohtext neu laden (z.B. bei Tabak-WBS)"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors disabled:opacity-50"
                  data-testid="reparse-colli-btn"
                >
                  {reparsingColli ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Colli neu laden
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Quick-Search Paket ───────────────────────────────────── */}
        {packageGroups.length > 1 && (
          <div className="relative" data-testid="pak-search-wrapper">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={pakSearch}
              onChange={e => setPakSearch(e.target.value)}
              placeholder="Paket-Nr. oder Warenname (z.B. Marlboro)..."
              className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all"
              data-testid="pak-search-input"
            />
            {pakSearch && (
              <button
                onClick={() => setPakSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                data-testid="pak-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* ── Paket-Dashboard ─────────────────────────────────────── */}
        {filteredGroups.length > 0 ? (
          <div className="space-y-3" data-testid="package-dashboard">
            {filteredGroups.map(group => (
              <PackageCard
                key={group.id}
                group={group}
                isOpen={openPak === group.id}
                onToggle={() => setOpenPak(openPak === group.id ? null : group.id)}
                onCheck={handleCheck}
                updating={updating}
                isCompleted={isCompleted}
                onEditColli={handleEditColli}
                onToggleCategory={handleToggleCategory}
                tobaccoPriceChanges={tobaccoPriceChanges}
                outerHighlight={pakSearch.trim()}
              />
            ))}
          </div>
        ) : pakSearch ? (
          <div className="text-center py-8 bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl">
            <Search className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Kein Paket mit „{pakSearch}" gefunden</p>
            <p className="text-xs text-gray-400 mt-1">Suche nach Paket-Endnummer oder Warenname</p>
            <button onClick={() => setPakSearch('')} className="mt-2 text-xs text-primary hover:underline" data-testid="pak-search-reset">Filter zurücksetzen</button>
          </div>
        ) : (
          <div className="text-center py-12 bg-white dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl">
            <Package className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Keine Artikel in dieser Lieferung</p>
            {!isCompleted && (
              <button
                onClick={handleReparseFull}
                disabled={reparsingFull}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-sm"
                data-testid="reparse-full-empty-btn"
              >
                {reparsingFull ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />}
                {reparsingFull ? 'Parst...' : 'Jetzt neu parsen'}
              </button>
            )}
          </div>
        )}

        {/* ── Lieferung Abschließen (nur wenn alle Pakete grün) ─── */}
        {!isCompleted && allPaksComplete && (
          <button
            onClick={handleComplete}
            disabled={completing}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl text-base font-bold bg-green-500 text-white hover:bg-green-600 shadow-lg shadow-green-500/25 transition-all active:scale-[0.98]"
            data-testid="complete-delivery-btn"
          >
            {completing
              ? <RefreshCw className="h-5 w-5 animate-spin" />
              : <ShieldCheck className="h-5 w-5" />}
            {completing ? 'Wird abgeschlossen...' : 'Lieferung abschließen'}
          </button>
        )}

        {/* ── Fehlmengen ──────────────────────────────────────────── */}
        {missingItems.length > 0 && (
          <div className="bg-white dark:bg-gray-900 border border-red-200 dark:border-red-800 rounded-2xl overflow-hidden" data-testid="missing-items-section">
            <div className="flex items-center gap-2 px-5 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-semibold text-red-700 dark:text-red-300">Nicht lieferbar ({missingItems.length})</span>
            </div>
            <div className="divide-y divide-red-50 dark:divide-red-900/20">
              {missingItems.map(m => (
                <div key={m.id} className="px-4 py-3" data-testid="missing-item-row">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">{m.article_name}</p>
                      {m.article_number && (
                        <p className="text-xs text-gray-400 font-mono mt-0.5">{m.article_number}</p>
                      )}
                    </div>
                  </div>
                  {m.reason && (
                    <span className="inline-block mt-1.5 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-100 dark:border-red-800 px-2 py-0.5 rounded-full leading-relaxed">
                      {m.reason}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Differenzen ─────────────────────────────────────────── */}
        {diffCount > 0 && (
          <div className="bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800 rounded-2xl overflow-hidden" data-testid="differences-section">
            <div className="flex items-center gap-2 px-5 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">Differenzen ({diffCount})</span>
            </div>
            <div className="divide-y divide-amber-50 dark:divide-amber-900/20">
              {items.filter(i => i.has_difference).map(item => (
                <div key={item.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{item.article_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Soll: {item.gesamt_menge} | Ist: {item.actual_quantity}</p>
                  </div>
                  <span className={`text-sm font-semibold ${item.difference_amount > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                    {item.difference_amount > 0 ? '+' : ''}{item.difference_amount}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── MODALS ───────────────────────────────────────────────── */}

      {mhdPromptItem && (
        <MhdPromptModal
          item={mhdPromptItem}
          onYes={handleMhdPromptYes}
          onNo={handleMhdPromptNo}
          onNeverAsk={handleMhdNeverAsk}
        />
      )}

      {mhdInputItem && (
        <MhdInputModal
          item={mhdInputItem}
          onSubmit={handleMhdSubmit}
          onSkip={handleMhdSkip}
          locationId={delivery?.location_id}
        />
      )}

      {eanItem && (
        <EanModal
          item={eanItem}
          onSubmit={handleEanSubmit}
          onSkip={handleEanSkip}
        />
      )}

      {colliEditPkg && (
        <Modal
          title="Colli-ID bearbeiten"
          subtitle={`PAK ${colliEditPkg.pak_number}`}
          onClose={() => setColliEditPkg(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Geben Sie die 12-stellige Colli-ID vom Paket-Etikett ein:
            </p>
            <input
              type="text"
              value={colliInput}
              onChange={e => setColliInput(e.target.value)}
              placeholder="z.B. 729347092370"
              className="w-full px-4 py-3 text-lg font-mono tracking-wider text-center border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/40 focus:border-primary/40"
              autoFocus
              maxLength={20}
              data-testid="colli-edit-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setColliEditPkg(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                data-testid="colli-edit-cancel"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveColli}
                disabled={!colliInput.trim()}
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="colli-edit-save"
              >
                Speichern
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* ── NEUPREIS-Kenntnisnahme (Tabak) ─────────────────────────── */}
      {priceGate && (
        <Modal
          title="Neupreis erkannt"
          subtitle="Tabak – bitte bestätigen"
          onClose={() => setPriceGate(null)}
        >
          <div className="space-y-4" data-testid="price-change-dialog">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
              <TrendingUp className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-testid="price-change-article">
                  {priceGate.change?.article_name || priceGate.item?.article_name}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">
                  bisher <span className="font-semibold">{Number(priceGate.change?.old_price).toFixed(2)} €</span>
                  {'  →  '}
                  neu <span className="font-semibold text-orange-600 dark:text-orange-400">{Number(priceGate.change?.new_price).toFixed(2)} €</span>
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Bitte die neu gelieferte Ware <strong>nach hinten</strong> legen und zuerst den
              Altbestand zum alten Preis abverkaufen. Falls dies eine <strong>Fehlzuordnung</strong> ist
              (z.&nbsp;B. andere Packungsgröße), wähle „Nein, falsch zugeordnet".
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handlePriceAck}
                className="w-full px-4 py-3 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-colors"
                data-testid="price-change-confirm-btn"
              >
                Ja, gesehen – Neupreis
              </button>
              <button
                onClick={handlePriceReject}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                data-testid="price-change-reject-btn"
              >
                Nein, falsch zugeordnet – kein Neupreis
              </button>
              <button
                onClick={() => setPriceGate(null)}
                className="w-full px-4 py-2 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                data-testid="price-change-cancel-btn"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
